<template>
  <div class="c_related_products">
    <div class="c_related_products__title">
      <h3>{{ $t('Related Products') }}</h3>
    </div>
    <div class="c_wrapper">
      <!-- Category buttons -->
      <div class="c_related_products__cat_buttons" v-if="data.displayFilters">
        <button
          v-for="categoryTitle in categoryTitles"
          :key="categoryTitle"
          :class="[
            'c_related_products__cat_button',
            { 'c_related_products__cat_button--active': categoryTitle === activeCategory },
          ]"
          @click="
            selectedCategory = categoryTitle;
            keyVal++;
          "
        >
          {{ categoryTitle }}
        </button>
      </div>

      <div class="c_related_products__cat_buttons--mobile" v-if="data.displayFilters">
        <accordion>
          <accordion-item
            :key="`accordion-item-0`"
            :item-index="0"
            open-behavior="none"
            :allow-multiple="true"
            :ref="'mobileLinks'"
            theme="lightMode"
          >
            <accordion-title slot="title">
              <div class="c_accordion_v2__title">
                <span>{{ $t('Filters') }}</span>
              </div>
            </accordion-title>
            <accordion-content slot="content">
              <button
                :key="'all'"
                :class="[
                  'c_related_products__cat_button',
                  { 'c_related_products__cat_button--active': activeCategory === 'all' },
                ]"
                @click="
                  selectedCategory = 'all';
                  keyVal++;
                "
              >
                {{ $t('All') }} {{ $t('Products') }}
              </button>
              <button
                v-for="categoryTitle in categoryTitles"
                :key="categoryTitle"
                :class="[
                  'c_related_products__cat_button',
                  { 'c_related_products__cat_button--active': categoryTitle === activeCategory },
                ]"
                @click="
                  selectedCategory = categoryTitle;
                  keyVal++;
                "
              >
                {{ categoryTitle }}
              </button>
            </accordion-content>
          </accordion-item>
        </accordion>
      </div>

      <!-- Products for selected category -->
      <client-only>
        <div v-if="selectedProducts && selectedProducts.length" class="c_agile_slider_2022">
          <agile
            ref="relatedProductsSlider"
            :options="agileOptions"
            :key="keyVal"
            @after-change="getCurrentSlide($event)"
          >
            <RelatedProductV2
              class="slide"
              v-for="(product, index) in selectedProducts"
              :key="index"
              :product="product"
            />
          </agile>
          <div
            class="c_related_products__footer"
            v-if="data.displayViewAllLink || getSlideCount > agileOptions?.slidesToShow"
          >
            <div
              class="c_related_products__footer__view_all"
              v-if="data.displayViewAllLink && viewAllUrl"
              :class="{
                'c_related_products__footer__view_all--mt': viewAllUrl && getSlideCount <= agileOptions.slidesToShow,
              }"
            >
              <a v-if="viewAllUrl" :href="viewAllUrl">{{ $t('View All Products') }}</a>
            </div>
            <SlideNumbers
              :number-of-slides="getSlideCount"
              :current-slide="currentSlide"
              v-if="getSlideCount > agileOptions?.slidesToShow"
            />
          </div>
        </div>
        <!-- Fallback if there are no products -->
        <p v-else>{{ $t('No results were returned.') }}</p>
      </client-only>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref, useContext } from '@nuxtjs/composition-api';
import RelatedProductV2 from 'Components/related_products_v2/RelatedProductV2.vue';
import { VueAgile } from 'vue-agile';
import SlideNumbers from 'Components/slider/SlideNumbers.vue';
import Accordion from 'Components/atom_accordion/Accordion.vue';
import AccordionContent from 'Components/atom_accordion/AccordionContent.vue';
import AccordionItem from 'Components/atom_accordion/AccordionItem.vue';
import AccordionTitle from 'Components/atom_accordion/AccordionTitle.vue';

export default defineComponent({
  name: 'RelatedProductsV2',
  props: {
    data: { type: Object, default: () => {} },
  },
  components: {
    RelatedProductV2,
    agile: VueAgile,
    SlideNumbers,
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTitle,
  },
  setup(props) {
    const { app } = useContext();
    const keyVal = ref(0);
    const mqSmall = 700;
    const mqMedium = 1024;
    const mq = ref(null);
    const currentSlide = ref(0);

    const getSlideCount = computed(() => selectedProducts.value.length);

    const agileOptions = computed(() => {
      const width = mq.value ?? 1920;
      let slidesToShow;

      if (getSlideCount.value <= 1) {
        slidesToShow = 1;
      } else if (getSlideCount.value === 2) {
        if (width <= mqSmall) {
          slidesToShow = 1;
        } else {
          slidesToShow = 2;
        }
      } else {
        if (width <= mqSmall) {
          slidesToShow = 1;
        } else if (width < mqMedium) {
          slidesToShow = 2;
        } else {
          slidesToShow = 3;
        }
      }

      return {
        infinite: true,
        slidesToShow,
        navButtons: getSlideCount.value > slidesToShow,
        dots: false,
      };
    });

    const getWindowDimensions = () => {
      if (typeof window !== 'undefined') {
        mq.value = document.documentElement.clientWidth;
      }
    };

    const depthMode = computed(() => props.data?.filterCategoryDepth ?? 'secondCat');

    const getGroupingCategory = (product) => {
      const cats = product?.productCategories ?? [];
      if (!cats.length) return null;

      // Ensure consistent order. We're already sorting in the GQL request, so this may be redundant.
      const sorted = [...cats].sort((a, b) => (a.level ?? 0) - (b.level ?? 0));

      const mode = depthMode.value;

      // Map modes to explicit levels
      const levelMap = {
        firstCat: 1,
        secondCat: 2,
        thirdCat: 3,
      };

      if (mode === 'deepestCat') {
        return sorted[sorted.length - 1] || null;
      }

      const targetLevel = levelMap[mode] ?? 2;

      // Prefer exact match
      const exact = sorted.find((c) => c.level === targetLevel);
      if (exact) return exact;

      // otherwise fall back to the deepest available
      return sorted[sorted.length - 1] || null;
    };

    const relatedProductsByCategory = computed(() => {
      const groups = {};

      props.data?.relatedProducts?.forEach((product) => {
        const groupingCategory = getGroupingCategory(product);
        if (!groupingCategory) return;

        const categoryTitle = groupingCategory.categoryTitle;

        if (!groups[categoryTitle]) {
          groups[categoryTitle] = {
            category: groupingCategory,
            products: [],
          };
        }

        groups[categoryTitle].products.push(product);
      });

      return groups;
    });

    const getAllRelatedProducts = computed(() => {
      return props.data?.relatedProducts ?? [];
    });

    // List of category titles (for buttons)
    const categoryTitles = computed(() => Object.keys(relatedProductsByCategory.value));

    // Track which category is currently selected
    const selectedCategory = ref(null);

    // If `displayFilters` is disabled from props, show all products
    if (!props.data?.displayFilters) {
      selectedCategory.value = 'all';
    }

    // Keep selectedCategory in sync with available categories
    const activeCategory = computed(() => {
      const titles = categoryTitles.value;
      if (!titles.length) return null;

      if (selectedCategory.value === 'all') return 'all';
      if (selectedCategory.value && titles.includes(selectedCategory.value)) return selectedCategory.value;

      return titles[0];
    });

    // Products for the active category
    const selectedProducts = computed(() => {
      if (!activeCategory.value) return [];
      if (activeCategory.value === 'all') return getAllRelatedProducts.value;

      return relatedProductsByCategory.value[activeCategory.value]?.products || [];
    });

    const viewAllUrl = computed(() => {
      if (!activeCategory.value) return null;
      return relatedProductsByCategory.value[activeCategory.value]?.category?.url || null;
    });

    const getCurrentSlide = (e) => {
      return (currentSlide.value = e.currentSlide + 1);
    };

    return {
      activeCategory,
      agileOptions,
      categoryTitles,
      currentSlide,
      getCurrentSlide,
      getSlideCount,
      getWindowDimensions,
      keyVal,
      selectedCategory,
      selectedProducts,
      viewAllUrl,
    };
  },
  mounted() {
    window.addEventListener('resize', this.getWindowDimensions);
  },
  unmounted() {
    window.removeEventListener('resize', this.getWindowDimensions);
  },
  created() {
    this.getWindowDimensions();
  },
});
</script>

<style lang="scss">
.c_related_products {
  --slider-arrow-distance-offset-lg: -20px;
  padding: 0 0 px_to_rem(40) 0;
  @include mq($mq_sm_to_md) {
    padding: 0 px_to_rem(40) px_to_rem(60) px_to_rem(40);
  }
  &__title {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: px_to_rem(32);
    @include mq($mq_sm_to_md) {
      margin-bottom: px_to_rem(40);
    }
  }
  h3 {
    @include fs_lh(16, 24, 16, 24);
    @include font('graphik_medium');
    text-align: center;
    padding: px_to_rem(20) px_to_rem(40);
    background-color: var(--color-gray_247);
    border-bottom-left-radius: px_to_rem(8);
    border-bottom-right-radius: px_to_rem(8);
  }
  &__cat_buttons {
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: px_to_rem(30);
    margin-bottom: px_to_rem(40);
    display: none;
    @include mq($mq_sm_to_md) {
      display: flex;
    }
    &--mobile {
      display: block;
      @include mq($mq_sm_to_md) {
        display: none;
      }
    }
  }
  &__cat_button {
    background: transparent;
    border-top: none;
    border-left: none;
    border-right: none;
    border-bottom: 2px solid transparent;
    outline: none;
    text-align: center;
    padding: px_to_rem(20);
    @include fs_lh(18, 28, 18, 28);
    @include font('graphik_medium');
    &--active {
      border-bottom-color: $color_black;
    }
  }
  .c_agile_slider_2022 .agile__slides {
    justify-content: center;
  }
  .c_agile_slider_2022 .agile .agile__actions .agile__nav-button {
    --slider-arrow-distance-offset-lg: -10px;
    --slider-arrow-distance-offset-md: -10px;
    &:hover {
      background-color: rgba(255, 201, 64, 1);
    }
  }
  .c_atom_accordion__content > div {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    button {
      text-align: left;
      padding: 0;
      margin-bottom: px_to_rem(20);
    }
  }
  &__footer {
    display: flex;
    margin-top: 0;
    flex-direction: column;
    padding-left: 0;
    padding-right: 0;
    @include mq($mq_sm_to_md) {
      margin-top: px_to_rem(40);
      padding-left: px_to_rem(10);
      padding-right: px_to_rem(10);
      flex-direction: row;
    }
    a,
    a:visited,
    a:active {
      color: var(--color-black);
      @include font('graphik_medium');
      @include fs_lh(16, 24, 16, 24);
      &:hover {
        text-decoration: none;
      }
    }
    &__view_all {
      order: 2;
      text-align: center;
      @include mq($mq_sm_to_md) {
        order: 1;
        text-align: inherit;
      }
      &--mt {
        margin-top: px_to_rem(20);
        @include mq($mq_sm_to_md) {
          margin-top: 0;
        }
      }
    }
    .c_slider__slide_numbers {
      margin-top: 0;
      margin-left: 0;
      order: 1;
      @include mq($mq_sm_to_md) {
        margin-left: auto;
        order: 2;
      }
    }
  }
  .c_slider__slide_numbers.c_2022--lead {
    @include font('graphik_regular');
    @include fs_lh(24, 32, 24, 32);
    .c_slider__number_divider {
      color: var(--color-black);
      padding: 0 px_to_rem(4);
    }
  }
}
</style>
